import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";

import { Button, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import { useLazyQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import { LINEAR_GRADIENT_BG } from "../../../../const";
import CustomButton from "../../../../components/CustomButton";
import { ReactIcons } from "../../../../icons";
import { GET_ATTRIBUTES } from "../../../../apollo/attribute/query";
import { CREATE_ATTRIBUTE, DELETE_ATTRIBUTE, UPDATE_ATTRIBUTE } from "../../../../apollo/attribute/mutation";
import { GET_ATTRIBUTEVALUES } from "../../../../apollo/attributeValue/query";
import {
  CREATE_ATTRIBUTEVALUE,
  DELETE_ATTRIBUTEVALUE,
  UPDATE_ATTRIBUTEVALUE,
} from "../../../../apollo/attributeValue/mutation";
import { MSG_CREATE_SUCCESS, MSG_INCOMPLETE_DATA, MSG_INTERNAL_ERROR } from "../../../../const/msg";
import AddAttributeModal from "./AddAttributeModal";
import EditAttributeModal from "./EditAttributeModal";
import AddAttributeValueModal from "./AddAttributeValueModal";
import EditAttributeValueModal from "./EditAttributeValueModal";

function normalizeValues(valuesText) {
  return valuesText
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

export default function OptionValue() {
  const [searchAttribute, setSearchAttribute] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [editingAttributeId, setEditingAttributeId] = useState("");
  const [formAttributeName, setFormAttributeName] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingAttributeValueId, setEditingAttributeValueId] = useState("");
  const [selectedAttributeId, setSelectedAttributeId] = useState("");
  const [formValuesText, setFormValuesText] = useState("");

  const [getAttributes, { data: attributesData, loading: loadingAttributes }] = useLazyQuery(
    GET_ATTRIBUTES,
    { fetchPolicy: "cache-and-network" }
  );

  const [createAttribute, { loading: loadingCreateAttribute }] = useMutation(CREATE_ATTRIBUTE);
  const [updateAttribute, { loading: loadingUpdateAttribute }] = useMutation(UPDATE_ATTRIBUTE);
  const [deleteAttribute, { loading: loadingDeleteAttribute }] = useMutation(DELETE_ATTRIBUTE);

  const [getAttributeValues, { data: attributeValuesData, loading: loadingAttributeValues }] = useLazyQuery(
    GET_ATTRIBUTEVALUES,
    { fetchPolicy: "cache-and-network" }
  );

  const [createAttributeValue, { loading: loadingCreateAttributeValue }] = useMutation(
    CREATE_ATTRIBUTEVALUE
  );
  const [updateAttributeValue, { loading: loadingUpdateAttributeValue }] = useMutation(
    UPDATE_ATTRIBUTEVALUE
  );
  const [deleteAttributeValue, { loading: loadingDeleteAttributeValue }] = useMutation(
    DELETE_ATTRIBUTEVALUE
  );

  const shopId = useMemo(() => JSON.parse(localStorage.getItem("SHOP_DATA"))?.id ?? "", []);

  useEffect(() => {
    if (!shopId) return;
    getAttributes({
      variables: {
        where: {
          shop: shopId,
          isDeleted: false,
        },
      },
    });
  }, [getAttributes, shopId]);

  const reloadAttributeValues = useCallback(async () => {
    if (!shopId) return;
    await getAttributeValues({
      variables: {
        where: {
          shop: shopId,
          isDeleted: false,
        },
        orderBy: "createdAt_DESC",
        limit: 1000,
      },
    });
  }, [getAttributeValues, shopId]);

  const attributes = useMemo(() => {
    const list = attributesData?.attributes?.data ?? [];
    return list
      .map((a) => ({
        id: a?.id ?? "",
        name: (a?.name ?? "").trim(),
        createdAt: a?.createdAt ?? null,
        updatedAt: a?.updatedAt ?? a?.createdAt ?? null,
      }))
      .filter((a) => a.id && a.name);
  }, [attributesData]);

  useEffect(() => {
    if (selectedAttributeId) return;
    if (attributes.length === 0) return;
    setSelectedAttributeId(attributes[0].id);
  }, [attributes, selectedAttributeId]);

  useEffect(() => {
    reloadAttributeValues();
  }, [reloadAttributeValues]);

  const filteredAttributes = useMemo(() => {
    const q = searchAttribute.trim().toLowerCase();
    if (!q) return attributes;
    return attributes.filter((a) => a.name.toLowerCase().includes(q));
  }, [attributes, searchAttribute]);

  const selectedAttributeName = useMemo(() => {
    const found = attributes.find((a) => a.id === selectedAttributeId);
    return found?.name ?? "";
  }, [attributes, selectedAttributeId]);

  const attributeValues = useMemo(() => {
    const list = attributeValuesData?.attributeValues?.data ?? [];
    return list
      .map((av) => ({
        id: av?.id ?? "",
        value: (av?.value ?? "").trim(),
        attributeId: av?.attribute?.id ?? "",
        attributeName: (av?.attribute?.name ?? "").trim(),
        createdAt: av?.createdAt ?? null,
        updatedAt: av?.updatedAt ?? null,
      }))
      .filter((av) => av.id && av.value && av.attributeId && av.attributeName);
  }, [attributeValuesData]);

  const groupedValues = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    const filtered = !q
      ? attributeValues
      : attributeValues.filter(
          (v) =>
            (v.value ?? "").toLowerCase().includes(q) || (v.attributeName ?? "").toLowerCase().includes(q)
        );

    const groups = new Map();
    for (const v of filtered) {
      const key = v.attributeId;
      if (!groups.has(key)) {
        groups.set(key, { attributeId: v.attributeId, attributeName: v.attributeName, items: [] });
      }
      groups.get(key).items.push(v);
    }

    const result = Array.from(groups.values());
    result.sort((a, b) => a.attributeName.localeCompare(b.attributeName));
    for (const g of result) {
      g.items.sort((a, b) => {
        const aTime = a.updatedAt || a.createdAt || "";
        const bTime = b.updatedAt || b.createdAt || "";
        return String(bTime).localeCompare(String(aTime));
      });
    }
    return result;
  }, [attributeValues, searchValue]);

  const hasAnyAttributeValues = groupedValues.length > 0;

  const filteredValuesFlat = useMemo(() => {
    const out = [];
    for (const g of groupedValues) out.push(...g.items);
    return out;
  }, [groupedValues]);

  const openAdd = () => {
    setEditingAttributeValueId("");
    setFormValuesText("");
    if (!selectedAttributeId && attributes.length > 0) setSelectedAttributeId(attributes[0].id);
    setShowModal(true);
  };

  const openEdit = (attributeValue) => {
    setEditingAttributeValueId(attributeValue?.id ?? "");
    setFormValuesText(attributeValue?.value ?? "");
    if (attributeValue?.attributeId) setSelectedAttributeId(attributeValue.attributeId);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAttributeValueId("");
    setFormValuesText("");
  };

  const openAddAttribute = () => {
    setEditingAttributeId("");
    setFormAttributeName("");
    setShowAttributeModal(true);
  };

  const openEditAttribute = (attribute) => {
    setEditingAttributeId(attribute?.id ?? "");
    setFormAttributeName(attribute?.name ?? "");
    setShowAttributeModal(true);
  };

  const closeAttributeModal = () => {
    setShowAttributeModal(false);
    setEditingAttributeId("");
    setFormAttributeName("");
  };

  const onSaveAttribute = async () => {
    const name = formAttributeName.trim();
    if (!name || !shopId) {
      toast.warning(MSG_INCOMPLETE_DATA);
      return;
    }

    if (attributes.some((a) => a.name.toLowerCase() === name.toLowerCase())) {
      toast.warning("ຄຸນລັກສະນະນີ້ມີຢູ່ແລ້ວ");
      return;
    }

    try {
      const res = editingAttributeId
        ? await updateAttribute({
            variables: {
              where: { id: editingAttributeId },
              data: { name, shop: shopId },
            },
          })
        : await createAttribute({
            variables: {
              data: {
                name,
                shop: shopId,
              },
            },
          });

      await getAttributes({
        variables: {
          where: {
            shop: shopId,
            isDeleted: false,
          },
        },
      });

      const newId = res?.data?.createAttribute?.id ?? "";
      if (!editingAttributeId && newId) setSelectedAttributeId(newId);

      toast.success(MSG_CREATE_SUCCESS);
      closeAttributeModal();
    } catch (err) {
      toast.error(MSG_INTERNAL_ERROR);
    }
  };

  const onDeleteAttribute = async (attributeId) => {
    if (!shopId || !attributeId) return;
    if (loadingDeleteAttribute) return;

    try {
      await deleteAttribute({
        variables: {
          where: { id: attributeId },
        },
      });

      await getAttributes({
        variables: {
          where: {
            shop: shopId,
            isDeleted: false,
          },
        },
      });

      await reloadAttributeValues();

      if (selectedAttributeId === attributeId) {
        const next = attributes.find((a) => a.id !== attributeId)?.id ?? "";
        setSelectedAttributeId(next);
      }
    } catch (err) {
      toast.error(MSG_INTERNAL_ERROR);
    }
  };

  const onSave = async () => {
    const values = normalizeValues(formValuesText);
    if (!shopId || !selectedAttributeId || values.length === 0) {
      toast.warning(MSG_INCOMPLETE_DATA);
      return;
    }

    try {
      if (editingAttributeValueId) {
        await updateAttributeValue({
          variables: {
            where: { id: editingAttributeValueId },
            data: {
              value: values[0],
              shop: shopId,
              attribute: selectedAttributeId,
            },
          },
        });
      } else {
        for (const value of values) {
          await createAttributeValue({
            variables: {
              data: {
                value,
                shop: shopId,
                attribute: selectedAttributeId,
              },
            },
          });
        }
      }

      await reloadAttributeValues();

      toast.success(MSG_CREATE_SUCCESS);
      closeModal();
    } catch (err) {
      toast.error(MSG_INTERNAL_ERROR);
    }
  };

  const onDeleteValue = async (attributeValueId) => {
    if (!shopId || !attributeValueId) return;
    if (loadingDeleteAttributeValue) return;
    try {
      await deleteAttributeValue({
        variables: {
          where: { id: attributeValueId },
        },
      });

      await reloadAttributeValues();
    } catch (err) {
      toast.error(MSG_INTERNAL_ERROR);
    }
  };

  const disableAttributeModalActions = loadingCreateAttribute || loadingUpdateAttribute;
  const disableAttributeSave = !formAttributeName.trim() || !shopId || disableAttributeModalActions;

  const disableAttributeValueModalActions = loadingCreateAttributeValue || loadingUpdateAttributeValue;
  const disableAttributeValueSelect =
    !shopId || attributes.length === 0 || loadingAttributes || disableAttributeValueModalActions;
  const disableAttributeValueSave =
    !selectedAttributeId || normalizeValues(formValuesText).length === 0 || disableAttributeValueModalActions;

  return (
    <React.Fragment>
      <div className="main">
        <Row style={{ marginTop: "1em" }}>
          <Col xs={12} md={4}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: "bold" }}>ຄຸນລັກສະນະ</div>
              <CustomButton
                height={35}
                width={120}
                borderRadius={8}
                title="ເພີ່ມ"
                background={LINEAR_GRADIENT_BG}
                onClick={openAddAttribute}
                disabled={!shopId}
              />
            </div>
            <InputGroup style={{ marginTop: 10 }}>
              <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                <SearchIcon sx={{ color: "#6B7280" }} />
              </InputGroup.Text>
              <Form.Control
                placeholder="ຄົ້ນຫາຄຸນລັກສະນະ..."
                onChange={(e) => setSearchAttribute(e.target.value)}
                value={searchAttribute}
                style={{ borderLeft: "none" }}
              />
            </InputGroup>

            <Table
              hover
              style={{
                marginTop: "12px",
                borderCollapse: "collapse",
                width: "100%",
                cursor: "pointer",
                borderRadius: "20px",
              }}
            >
              <thead
                style={{
                  background: LINEAR_GRADIENT_BG,
                  borderRadius: " 8px 8px 0px 0px",
                  color: "#FFFFFF",
                }}
              >
                <tr>
                  <th style={{ verticalAlign: "middle", fontSize: 13 }}>ລ/ດ</th>
                  <th style={{ verticalAlign: "middle", fontSize: 13 }}>ຄຸນລັກສະນະ</th>
                    <th style={{ verticalAlign: "middle", fontSize: 13 }}>ວັນທີ່</th>
                    <th style={{ verticalAlign: "middle", fontSize: 13 }}>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttributes.length > 0 ? (
                  filteredAttributes.map((a, index) => (
                    <tr
                      key={a.id}
                      onClick={() => setSelectedAttributeId(a.id)}
                      style={{
                        background: a.id === selectedAttributeId ? "#F3F4F6" : "transparent",
                      }}
                    >
                      <td style={{ verticalAlign: "middle", fontSize: 13 }}>{index + 1}</td>
                      <td style={{ verticalAlign: "middle", fontSize: 13 }}>{a.name}</td>
                        <td style={{ verticalAlign: "middle", fontSize: 13 }}>
                          {a.updatedAt || a.createdAt
                            ? moment(a.updatedAt || a.createdAt).format("DD/MM/YYYY")
                            : "-"}
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <Button
                              variant="outline-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditAttribute(a);
                              }}
                            >
                              <ReactIcons.FaEdit />
                            </Button>
                            <Button
                              variant="outline-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteAttribute(a.id);
                              }}
                              disabled={loadingDeleteAttribute}
                            >
                              <ReactIcons.MdDeleteOutline />
                            </Button>
                          </div>
                        </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                      <td colSpan={4} style={{ textAlign: "center", verticalAlign: "middle", fontSize: 13 }}>
                      {loadingAttributes ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບຂໍ້ມູນ"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>

          <Col xs={12} md={8}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: "bold" }}>
                ຄ່າຄຸນລັກສະນະ
              </div>
              <CustomButton
                height={40}
                width={220}
                borderRadius={8}
                title="ເພີ່ມຄ່າຄຸນລັກສະນະ"
                background={LINEAR_GRADIENT_BG}
                onClick={openAdd}
                disabled={!shopId || attributes.length === 0}
              />
            </div>

            <InputGroup style={{ marginTop: 10 }}>
              <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                <SearchIcon sx={{ color: "#6B7280" }} />
              </InputGroup.Text>
              <Form.Control
                placeholder="ຄົ້ນຫາຄ່າ..."
                onChange={(e) => setSearchValue(e.target.value)}
                value={searchValue}
                style={{ borderLeft: "none" }}
              />
            </InputGroup>

            <Table
              hover
              style={{
                marginTop: "12px",
                borderCollapse: "collapse",
                width: "100%",
                cursor: "pointer",
                borderRadius: "20px",
              }}
            >
              <thead
                style={{
                  background: LINEAR_GRADIENT_BG,
                  borderRadius: " 8px 8px 0px 0px",
                  color: "#FFFFFF",
                }}
              >
                <tr>
                  <th style={{ verticalAlign: "middle", fontSize: 13 }}>ລ/ດ</th>
                  <th style={{ verticalAlign: "middle", fontSize: 13 }}>ຄຸນລັກສະນະ</th>
                  <th style={{ verticalAlign: "middle", fontSize: 13 }}>ຄ່າ</th>
                  <th style={{ verticalAlign: "middle", fontSize: 13 }}>ວັນທີ່</th>
                  <th style={{ verticalAlign: "middle", fontSize: 13 }}>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {hasAnyAttributeValues ? (
                  groupedValues.map((group) => (
                    <React.Fragment key={group.attributeId}>
                      <tr style={{ background: "#F9FAFB" }}>
                        <td colSpan={5} style={{ verticalAlign: "middle", fontSize: 13, fontWeight: "bold" }}>
                          {group.attributeName}
                        </td>
                      </tr>
                      {group.items.map((v, index) => (
                        <tr key={v.id}>
                          <td style={{ verticalAlign: "middle", fontSize: 13 }}>{index + 1}</td>
                          <td style={{ verticalAlign: "middle", fontSize: 13 }}>{v.attributeName}</td>
                          <td style={{ verticalAlign: "middle", fontSize: 13 }}>{v.value}</td>
                          <td style={{ verticalAlign: "middle", fontSize: 13 }}>
                            {v.updatedAt || v.createdAt
                              ? moment(v.updatedAt || v.createdAt).format("DD/MM/YYYY")
                              : "-"}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <Button variant="outline-primary" onClick={() => openEdit(v)}>
                                <ReactIcons.FaEdit />
                              </Button>
                              <Button variant="outline-danger" onClick={() => onDeleteValue(v.id)}>
                                <ReactIcons.MdDeleteOutline />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", verticalAlign: "middle", fontSize: 13 }}>
                      {loadingAttributeValues ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບຂໍ້ມູນ"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </div>

      <AddAttributeModal
        show={showAttributeModal && !editingAttributeId}
        onClose={closeAttributeModal}
        attributeName={formAttributeName}
        setAttributeName={setFormAttributeName}
        onSave={onSaveAttribute}
        disableClose={disableAttributeModalActions}
        disableSave={disableAttributeSave}
      />

      <EditAttributeModal
        show={showAttributeModal && Boolean(editingAttributeId)}
        onClose={closeAttributeModal}
        attributeName={formAttributeName}
        setAttributeName={setFormAttributeName}
        onSave={onSaveAttribute}
        disableClose={disableAttributeModalActions}
        disableSave={disableAttributeSave}
      />

      <AddAttributeValueModal
        show={showModal && !editingAttributeValueId}
        onClose={closeModal}
        attributes={attributes}
        selectedAttributeId={selectedAttributeId}
        setSelectedAttributeId={setSelectedAttributeId}
        valuesText={formValuesText}
        setValuesText={setFormValuesText}
        disableAttributeSelect={disableAttributeValueSelect}
        disableClose={disableAttributeValueModalActions}
        disableSave={disableAttributeValueSave}
        onSave={onSave}
      />

      <EditAttributeValueModal
        show={showModal && Boolean(editingAttributeValueId)}
        onClose={closeModal}
        attributes={attributes}
        selectedAttributeId={selectedAttributeId}
        setSelectedAttributeId={setSelectedAttributeId}
        valuesText={formValuesText}
        setValuesText={setFormValuesText}
        disableAttributeSelect={disableAttributeValueSelect}
        disableClose={disableAttributeValueModalActions}
        disableSave={disableAttributeValueSave}
        onSave={onSave}
      />
    </React.Fragment>
  );
}
